// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// CUSTOM-START: cl-cart-01
// Price a whole basket without charging. Send `{ cartItems: [{ listingId, quantity }],
// destinationCountry }` and nothing about money — the server reads prices, stock and shipping
// from the listings itself (AC-32), so anything else sent here is discarded rather than
// trusted. See server/api/custom/cart-line-items.js.
export const cartLineItems = body => {
  return post('/api/cart-line-items', body);
};

// Re-check every listing in a basket in one call. Returns 200 with a per-row error list even
// when rows are unavailable — a basket with a sold-out item is a valid question, and the cart
// page needs the specific reasons in order to show them (D-12, D-13).
export const cartAvailability = body => {
  return post('/api/cart-availability', body);
};

// Start a real, payable transaction for a basket. Send `{ cartItems, destinationCountry,
// shippingDetails }` — and nothing about money: prices, line items and the discount are all
// computed server-side from the listings it fetches itself (AC-32), so anything else sent here
// is ignored rather than trusted. Holds stock for the non-anchor items before initiating, and
// releases it again if anything fails (R4). See server/api/custom/cart-initiate.js.
export const cartInitiate = body => {
  return post('/api/cart-initiate', body);
};

// Give back stock held for a basket that will not be paid for (R4). Send only
// `{ transactionId }` — the amounts come from the transaction itself, never from here, because
// a caller able to name its own held amounts could inflate a seller's stock for free.
export const cartRelease = body => {
  return post('/api/cart-release', body);
};
// CUSTOM-END: cl-cart-01

// CUSTOM-START: cl-neg-01
// Open a buyer's offer on one listing or a single-seller bundle. Send `{ cartItems: [{
// listingId, quantity }], offerInSubunits, message?, destinationCountry? }` — nothing about
// price or shipping beyond the offer figure itself: the server re-fetches the listings, checks
// the seller's minimum through the Integration API, and re-derives everything else (SL8).
// Holds nothing (D12): an offer reserves no stock at any point. Stock is taken when the buyer
// starts paying, exactly as Buy It Now does. A retry against an already-open negotiation on the
// same anchor returns that transaction rather than opening a second one (EC12). See
// server/api/custom/offer-initiate.js.
export const offerInitiate = body => post('/api/offer-initiate', body);

// Move an existing negotiation on. Send `{ transactionId, transition }` plus, for a counter, the
// figures being countered with (`offerInSubunits`, and for a SELLER counter only
// `shippingInSubunits` — a buyer's copy of it is ignored, since shipping is a seller lever). Those
// figures are intent, not prices: the server floor-checks them and re-prices the basket from the
// transaction's own `protectedData.cart` snapshot, so no line item ever comes from here. No
// discount: D13 removed it from the offer flow; the server ignores the field if a caller sends
// one. `protectedData` is read on the payment joins
// only (the delivery address), and its `cart`/`negotiation` keys are stripped — the payment join
// takes no money from the body at all. An offer holds no stock (D12), so the terminal transitions
// release nothing. The payment joins take the non-anchor hand-holds and let the transition reserve
// the anchor natively — the same shape a Buy It Now basket checkout has. See
// server/api/custom/offer-transition.js.
export const offerTransition = body => post('/api/offer-transition', body);

// The seller's own offer, sent into an inquiry thread they are the provider of. Send
// `{ transactionId, offerInSubunits?, shippingInSubunits? }` — figures as INTENT, never as
// prices: the server re-fetches the listing, floor-checks the numbers and re-prices from its own
// copy (SL8). Omitting `offerInSubunits` means "at list price", computed server-side. No
// discount: D13 removed it from the offer flow; the server ignores the field if a caller sends
// one (`reasonKey` too — nothing reads it any more). See server/api/custom/offer-initiate.js's
// `sellerOffer`.
export const offerSend = body => post('/api/offer-send', body);
// CUSTOM-END: cl-neg-01

// CUSTOM-START: cl-list-01
// Delete one of the current user's own listings. Send `{ listingId }` (a uuid string) and nothing
// else: ownership is proved server-side with the user's own token. Published listings are closed
// and marked deleted in metadata; drafts are discarded for real. Safe to retry. See
// server/api/custom/delete-own-listing.js.
export const deleteOwnListing = body => post('/api/delete-own-listing', body);
// CUSTOM-END: cl-list-01
